import React, { useEffect } from 'react';

/**
 * Adds custom focus and blur listeners to the input element
 * to account for any attached popover behavior.
 * The input loses focus when clicking *in* the popover,
 * so we need to handle this special case instead of simple onBlur.
 * @param ref The reference to the element that opens the popover (button or input)
 * @param onFocus The focus callback
 * @param onBlur The blur callback
 */
export const usePopoverFocus = (
  ref: React.RefObject<HTMLInputElement | HTMLButtonElement>,
  onFocus: () => void,
  onBlur: () => void,
) => {
  useEffect(() => {
    const input = ref.current;
    let blurFromClick = false;

    const focusListener = () => {
      blurFromClick = false;
      onFocus();
    };

    const blurListener = () => {
      if (blurFromClick) {
        blurFromClick = false;
      } else {
        onBlur();
      }
    };

    const clickListener = (event: MouseEvent | TouchEvent) => {
      if (event.target !== input) {
        blurFromClick = true;
      }
    };

    input?.addEventListener('focus', focusListener);
    input?.addEventListener('blur', blurListener);
    document.addEventListener('mousedown', clickListener);
    document.addEventListener('touchstart', clickListener);

    return () => {
      input?.removeEventListener('focus', focusListener);
      input?.removeEventListener('blur', blurListener);
      document.removeEventListener('mousedown', clickListener);
      document.removeEventListener('touchstart', clickListener);
    };
  }, [ref, onBlur, onFocus]);
};

/**
 * A modulus function that accounts for negatives.
 * @param num The number to mod.
 * @param mod The mod value.
 */
export const mod = (num: number, mod: number) => {
  return ((num % mod) + mod) % mod;
};
